/**
 * Subscription Context
 * Manages subscription state and entitlements throughout the app
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  SUBSCRIPTION_TIERS,
  TIER_CONFIG,
  getTierConfig,
  isRelationshipTypeAvailable
} from '../config/subscriptionTiers';

interface Usage {
  members: number;
  events: number;
  relationshipTypes: string[];
}

interface UpgradeReason {
  type: 'members' | 'events' | 'relationshipType' | 'feature';
  [key: string]: any;
}

interface SubscriptionContextType {
  subscriptionTier: string;
  tierConfig: any;
  usage: Usage;
  canAddMember: () => boolean;
  canCreateEvent: () => boolean;
  canUseRelationshipType: (relationshipTypeId: string) => boolean;
  hasFeature: (featureName: string) => boolean;
  getUsagePercentage: (resourceType: 'members' | 'events' | 'relationshipTypes') => number;
  updateUsage: (newUsage: Partial<Usage>) => void;
  showUpgradeModal: boolean;
  promptUpgrade: (reason: UpgradeReason) => void;
  closeUpgradeModal: () => void;
  upgradeTier: (newTier: string) => void;
  getUpgradeMessage: () => any;
  limits: {
    members: number;
    events: number;
    relationshipTypes: number;
  };
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // In production, this would come from the database via API
  // For now, we'll use localStorage to simulate subscription state
  const [subscriptionTier, setSubscriptionTier] = useState(
    localStorage.getItem('subscription_tier') || SUBSCRIPTION_TIERS.STARTER
  );

  const [usage, setUsage] = useState<Usage>({
    members: 0,
    events: 0,
    relationshipTypes: []
  });

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<UpgradeReason | null>(null);

  // Get current tier configuration
  const tierConfig = getTierConfig(subscriptionTier);

  /**
   * Load usage stats from API or localStorage
   */
  useEffect(() => {
    // In production, this would be an API call
    const storedUsage = localStorage.getItem('subscription_usage');
    if (storedUsage) {
      try {
        setUsage(JSON.parse(storedUsage));
      } catch (e) {
        console.error('Failed to parse usage data:', e);
      }
    }
  }, []);

  /**
   * Update usage statistics
   */
  const updateUsage = useCallback((newUsage: Partial<Usage>) => {
    setUsage(prev => {
      const updated = { ...prev, ...newUsage };
      localStorage.setItem('subscription_usage', JSON.stringify(updated));
      return updated;
    });
  }, []);

  /**
   * Check if user can add a new member
   */
  const canAddMember = useCallback(() => {
    const limit = tierConfig.features.maxMembers;
    return usage.members < limit;
  }, [tierConfig, usage.members]);

  /**
   * Check if user can create a new event
   */
  const canCreateEvent = useCallback(() => {
    const limit = tierConfig.features.maxEvents;
    return usage.events < limit;
  }, [tierConfig, usage.events]);

  /**
   * Check if user can use a specific relationship type
   */
  const canUseRelationshipType = useCallback((relationshipTypeId: string) => {
    return isRelationshipTypeAvailable(relationshipTypeId, subscriptionTier);
  }, [subscriptionTier]);

  /**
   * Check if a feature is available in current tier
   */
  const hasFeature = useCallback((featureName: string) => {
    return (tierConfig.features as any)[featureName] === true;
  }, [tierConfig]);

  /**
   * Get usage percentage for a resource
   */
  const getUsagePercentage = useCallback((resourceType: 'members' | 'events' | 'relationshipTypes') => {
    switch (resourceType) {
      case 'members':
        const memberLimit = tierConfig.features.maxMembers;
        return memberLimit === 999999 ? 0 : (usage.members / memberLimit) * 100;
      case 'events':
        const eventLimit = tierConfig.features.maxEvents;
        return eventLimit === 999999 ? 0 : (usage.events / eventLimit) * 100;
      case 'relationshipTypes':
        const typeLimit = tierConfig.features.maxRelationshipTypes;
        return typeLimit === 999999 ? 0 : (usage.relationshipTypes.length / typeLimit) * 100;
      default:
        return 0;
    }
  }, [tierConfig, usage]);

  /**
   * Show upgrade prompt with specific reason
   */
  const promptUpgrade = useCallback((reason: UpgradeReason) => {
    setUpgradeReason(reason);
    setShowUpgradeModal(true);
  }, []);

  /**
   * Close upgrade modal
   */
  const closeUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false);
    setUpgradeReason(null);
  }, []);

  /**
   * Upgrade subscription tier (simulated for now)
   */
  const upgradeTier = useCallback((newTier: string) => {
    setSubscriptionTier(newTier);
    localStorage.setItem('subscription_tier', newTier);
    closeUpgradeModal();
  }, [closeUpgradeModal]);

  /**
   * Get upgrade message based on reason
   */
  const getUpgradeMessage = useCallback(() => {
    if (!upgradeReason) return null;

    const messages = {
      members: {
        title: 'Member Limit Reached',
        description: `You've reached the maximum of ${tierConfig.features.maxMembers} members on the ${subscriptionTier} plan.`,
        suggestion: 'Upgrade to add more team members!'
      },
      events: {
        title: 'Event Limit Reached',
        description: `You've reached the maximum of ${tierConfig.features.maxEvents} events on the ${subscriptionTier} plan.`,
        suggestion: 'Upgrade for unlimited events!'
      },
      relationshipType: {
        title: 'Premium Relationship Type',
        description: 'This relationship type is only available on Premium and Enterprise plans.',
        suggestion: 'Unlock more collaboration types with an upgrade!'
      },
      feature: {
        title: 'Premium Feature',
        description: 'This feature is only available on higher-tier plans.',
        suggestion: 'Upgrade to unlock advanced features!'
      }
    };

    return messages[upgradeReason.type] || messages.feature;
  }, [upgradeReason, tierConfig, subscriptionTier]);

  const value = {
    // Subscription state
    subscriptionTier,
    tierConfig,
    usage,

    // Usage checks
    canAddMember,
    canCreateEvent,
    canUseRelationshipType,
    hasFeature,
    getUsagePercentage,

    // Usage updates
    updateUsage,

    // Upgrade management
    showUpgradeModal,
    promptUpgrade,
    closeUpgradeModal,
    upgradeTier,
    getUpgradeMessage,

    // Limits (for display)
    limits: {
      members: tierConfig.features.maxMembers,
      events: tierConfig.features.maxEvents,
      relationshipTypes: tierConfig.features.maxRelationshipTypes
    }
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
